import { ReactElement } from "react";
import {
  Sl1AnalysisResult as Sl1Result,
  ConvertResult as ConvertSl1Result,
  ConvertResultType,
} from "../../engines/sl1Engine";
import { useTranslation } from "react-i18next";
import {
  Box,
  Flex,
  Text,
  Image,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useTheme,
} from "@chakra-ui/react";
import abcdRiskBar from "../../assets/images/sl1/abcd-risk-bar.png";
import { Sl1HistoryTable } from "../atoms/Sl1HistoryTable";

type HistoryRecord = {
  date: string;
  score: number;
  evaluation: "A" | "B" | "C" | "D" | "-";
  threeMonthScore?: number;
  currentScore?: number;
};

const ColList = ["A", "Ga", "Ra", "Pa"] as const;

const TABLE_STYLES = {
  th: {
    base: {
      fontSize: "sm" as const,
      borderWidth: "1px",
      paddingY: 2,
      textTransform: "none" as const,
    },
    center: {
      textAlign: "center" as const,
      style: { paddingInlineStart: 0, paddingInlineEnd: 0 },
    },
    left: {
      textAlign: "left" as const,
      paddingLeft: 1,
    },
  },
  td: {
    base: {
      borderWidth: "1px",
      paddingY: 1,
      fontSize: "lm" as const,
    },
    center: {
      textAlign: "center" as const,
      style: { paddingInlineStart: 0, paddingInlineEnd: 0 },
    },
    left: {
      textAlign: "left" as const,
      paddingLeft: 1,
    },
  },
};

function Sl1MainResult({
  result,
}: Readonly<{
  result: ConvertResultType;
}>): ReactElement {
  const { t } = useTranslation();

  const sas_score = result.SleepQualityScore;
  const current_score = result.CurrentSleepQualityScore;

  return (
    <Flex
      direction="row"
      borderTop="2px solid"
      borderBottom="2px solid"
      borderColor="primary.theme_lv1"
      bg="white"
      px={4}
      py={0}
      gap={4}
      minHeight="200px"
    >
      <Box width="134px" height="220px" flexShrink={0}>
        <Image
          src={abcdRiskBar}
          alt="image"
          objectFit="contain"
          width="100%"
          height="100%"
          ml="20px"
        />
      </Box>
      <Flex direction="column" width="100%" position="relative" height="200px">
        {/* 3ヶ月平均セット */}
        <Flex
          direction="column"
          alignItems="center"
          position="absolute"
          top="20%"
          left="50%"
          transform="translateX(-50%)"
        >
          <Text
            fontSize="6xl"
            fontWeight="light"
            lineHeight="1"
            fontFamily="Jost"
            color="#333333"
          >
            {typeof sas_score === "number" ? sas_score.toFixed(1) : sas_score}
          </Text>
          <Text fontSize="xs" color="#888888" mt={1}>
            {t("Result.Sl1.ThreeMonthAverage")}
          </Text>
        </Flex>

        {/* 今回セット */}
        {current_score !== undefined && (
          <Flex
            direction="column"
            alignItems="center"
            position="absolute"
            top="70%"
            left="50%"
            transform="translateX(-50%)"
          >
            <Text
              fontSize="2xl"
              fontWeight="light"
              lineHeight="1"
              fontFamily="Jost"
              color="#333333"
            >
              {current_score}
            </Text>
            <Text fontSize="xs" color="#888888" mt={1}>
              {t("Result.Sl1.Current")}
            </Text>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}

function Sl1ReliabilityTable({
  convertSl1Result,
}: Readonly<{
  convertSl1Result: ReturnType<typeof ConvertSl1Result>;
}>): ReactElement {
  const { t } = useTranslation();
  const theme = useTheme();

  // ColListを使って繰り返し処理
  const rowsData = ColList.map((col) => {
    const reliabilityScore =
      convertSl1Result[`Col${col}ReliabilityScore`].ReliableScore;
    const isReliable = convertSl1Result[`Col${col}ReliabilityScore`].IsReliable;

    // 信頼性スコアが数値の場合のみ判定
    const scoreNum =
      typeof reliabilityScore === "number"
        ? reliabilityScore
        : parseFloat(String(reliabilityScore));
    const isLowReliability = !isNaN(scoreNum) && scoreNum < 50;
    const reliabilityText =
      isReliable === true || isReliable === "true" || isReliable === "OK"
        ? t("Result.Sl1.Reliability.Accurate")
        : t("Result.Sl1.Reliability.MaybeInaccurate");

    return {
      phrase: t(`PJ.Sl1Phrase${col.toUpperCase()}.Phrase`),
      score: reliabilityScore,
      text: reliabilityText,
      isLow: isLowReliability,
    };
  });

  const tableReliabilityColumns = [
    {
      width: "25%",
      title: t("Result.Sl1.ReliabilityTable.SpeechContent"),
      align: "center" as const,
    },
    {
      width: "20%",
      title: t("Result.Sl1.ReliabilityTable.Reliability"),
      align: "center" as const,
    },
    {
      width: "55%",
      title: t("Result.Sl1.ReliabilityTable.ReliabilityOfSpeech"),
      align: "left" as const,
    },
  ];

  return (
    <Flex flexDirection="column" mt="20px">
      <Text mb="5px">{t("Result.Sl1.ReliabilityTable.Title")}</Text>
      <Table bg="common.base" fontSize="sm">
        <Thead bg={theme.colors.primary["theme_lv1"]}>
          <Tr>
            {tableReliabilityColumns.map((value, index) => (
              <Th
                key={index}
                width={value.width}
                {...TABLE_STYLES.th.base}
                {...(value.align === "left"
                  ? TABLE_STYLES.th.left
                  : TABLE_STYLES.th.center)}
                color="white"
                borderColor="#CCCCCC"
              >
                {value.title}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {rowsData.map((row, index) => (
            <Tr key={index}>
              <Td
                {...TABLE_STYLES.td.base}
                {...TABLE_STYLES.td.center}
                borderColor="#CCCCCC"
                borderRight="none"
              >
                {row.phrase}
              </Td>
              <Td
                {...TABLE_STYLES.td.base}
                {...TABLE_STYLES.td.center}
                borderColor="#CCCCCC"
                borderRight="none"
                borderLeft="none"
              >
                {row.score}
              </Td>
              <Td
                {...TABLE_STYLES.td.base}
                {...TABLE_STYLES.td.left}
                color={row.isLow ? "red.500" : "#555555"}
                borderColor="#CCCCCC"
                borderLeft="none"
              >
                {row.text}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Flex>
  );
}

function prepareHistoryRecords(
  convertSl1Result: ReturnType<typeof ConvertSl1Result>,
  sl1Result: Sl1Result | null,
): HistoryRecord[] {
  const getDecisionLabel = (decision: string): "A" | "B" | "C" | "D" | "-" => {
    const labelConfig: Record<string, "A" | "B" | "C" | "D"> = {
      CLS0_HEALTHY: "A",
      CLS1_MILD: "B",
      CLS2_MODERATE: "C",
      CLS3_SEVERE: "D",
    };
    return labelConfig[decision] ?? "-";
  };

  const pastData = convertSl1Result.PastData || [];

  // 最新のデータを作成（現在の結果から）
  const currentData =
    convertSl1Result.NinetyDayAverage && sl1Result
      ? {
          id: -1,
          recording_id: "",
          analyzed_at: sl1Result.analyzed_at || new Date().toISOString(),
          analysis_result: {
            decision: convertSl1Result.Decision,
            sleep_quality_score:
              Number(convertSl1Result.CurrentSleepQualityScore) / 100,
            ninety_day_average: convertSl1Result.NinetyDayAverage,
          },
        }
      : null;

  // 最新データと過去データを結合
  const allData = currentData ? [currentData, ...pastData] : pastData;

  // HistoryRecord形式に変換
  return allData.map((item) => {
    const analysisResult = item.analysis_result;
    const ninetyDayAvg = analysisResult.ninety_day_average;
    const avgScore: number =
      Math.round(ninetyDayAvg.sleep_quality_score * 1000) / 10;
    const avgDecision: string | undefined = ninetyDayAvg?.decision;
    const currentScore = Math.round(analysisResult.sleep_quality_score * 100);

    return {
      date: item.analyzed_at,
      score: avgScore,
      evaluation: avgDecision ? getDecisionLabel(avgDecision) : "-",
      threeMonthScore: avgScore,
      currentScore: currentScore,
    };
  });
}

export function ResultSl1({
  sl1Result,
}: Readonly<{
  sl1Result: Sl1Result | null;
}>): ReactElement {
  const { t } = useTranslation();
  const convertSl1Result = ConvertSl1Result({ result: sl1Result });

  return (
    <Flex flexDirection="column" mb="20px" color="#555555">
      {/* タイトル */}
      <Text fontSize="2xl" fontWeight="bold" mb="20px" textAlign="center">
        {t("PJ.Sl1Result.Title")}
      </Text>

      <Flex flexDirection="column" mb="10px">
        <Sl1MainResult result={convertSl1Result} />
      </Flex>

      <Box>
        <Sl1ReliabilityTable convertSl1Result={convertSl1Result} />
        <Sl1HistoryTable
          records={prepareHistoryRecords(convertSl1Result, sl1Result)}
        />

        {/* 説明テキスト */}
        <Box mt="20px" p="15px" borderRadius="md">
          <Text fontSize="sm" lineHeight="1.6">
            {t("Result.Sl1.Description1")}
          </Text>
          <Text fontSize="sm" lineHeight="1.6" mt={2}>
            {t("Result.Sl1.Description2")}
          </Text>
        </Box>
      </Box>
    </Flex>
  );
}
